import { Minus, Plus } from 'lucide-react';
import { formatBRL } from '../lib/format';
import { cx } from '../ui/primitives';

export type PayableItem = {
  id: string; productName: string; options: string[]; quantity: number; unitPriceCents: number; totalCents: number;
  cancelled: boolean; paidCents: number; reservedCents: number; remainingCents: number; remainingUnits: number;
};
/** Item escolhido → quantas unidades pagar agora. */
export type UnitSelection = Record<string, number>;

/** Mesmo cálculo do servidor: as últimas unidades levam exatamente o que falta (o servidor recalcula de qualquer forma). */
export function unitsCents(item: PayableItem, units: number) {
  return units >= item.remainingUnits ? item.remainingCents : units * item.unitPriceCents;
}
export function selectionCents(items: PayableItem[], sel: UnitSelection) {
  return items.reduce((s, i) => (sel[i.id] ? s + unitsCents(i, sel[i.id]!) : s), 0);
}
export function selectionPayload(sel: UnitSelection) {
  return Object.entries(sel).map(([id, units]) => ({ id, units }));
}

/**
 * Lista de itens para pagar "por item". Em linhas com várias unidades (ex.: 3× cerveja) dá para escolher quantas
 * pagar agora; as outras continuam na conta para depois.
 */
export function ItemUnitsPicker({ items, value, onChange, showStatus = false, accentClass = 'accent-[var(--brand)]', className }: {
  items: PayableItem[];
  value: UnitSelection;
  onChange: (v: UnitSelection) => void;
  showStatus?: boolean;
  accentClass?: string;
  className?: string;
}) {
  const set = (id: string, units: number | null) => {
    const next = { ...value };
    if (units === null) delete next[id];
    else next[id] = units;
    onChange(next);
  };
  return (
    <ul className={cx('divide-y divide-line rounded-xl border border-line text-sm', className)}>
      {items.map((i) => {
        const disabled = i.remainingCents <= 0;
        const units = value[i.id];
        const checked = units !== undefined;
        return (
          <li key={i.id} className={cx('px-3 py-2.5', disabled && 'opacity-50')}>
            <label className={cx('flex items-center gap-3', !disabled && 'cursor-pointer')}>
              <input
                type="checkbox"
                className={cx('size-5', accentClass)}
                disabled={disabled}
                checked={checked}
                onChange={(e) => set(i.id, e.target.checked ? i.remainingUnits : null)}
              />
              <span className="min-w-0 flex-1">
                {i.quantity}× {i.productName}
                {i.options.length > 0 && <span className="block text-xs text-ink-3">{i.options.join(' · ')}</span>}
                {!disabled && i.remainingUnits < i.quantity && <span className="block text-xs text-ink-3">Faltam {i.remainingUnits} de {i.quantity}</span>}
                {showStatus && i.paidCents > 0 && i.remainingCents === 0 && <span className="block text-xs text-sprout-600">Pago</span>}
                {showStatus && i.reservedCents > 0 && <span className="block text-xs text-warn-600">Sendo pago por alguém da mesa</span>}
              </span>
              <span className="tabular-nums">{formatBRL(checked ? unitsCents(i, units) : i.remainingCents || i.totalCents)}</span>
            </label>
            {checked && i.remainingUnits > 1 && (
              <div className="mt-2 flex items-center justify-end gap-2 text-ink-2">
                <span className="text-xs">Pagar</span>
                <button
                  type="button"
                  aria-label={`Menos uma unidade de ${i.productName}`}
                  disabled={units <= 1}
                  onClick={() => set(i.id, units - 1)}
                  className="grid size-8 place-items-center rounded-lg border border-line bg-white disabled:opacity-40"
                >
                  <Minus className="size-4" />
                </button>
                <span className="w-6 text-center font-semibold tabular-nums" aria-live="polite">{units}</span>
                <button
                  type="button"
                  aria-label={`Mais uma unidade de ${i.productName}`}
                  disabled={units >= i.remainingUnits}
                  onClick={() => set(i.id, units + 1)}
                  className="grid size-8 place-items-center rounded-lg border border-line bg-white disabled:opacity-40"
                >
                  <Plus className="size-4" />
                </button>
                <span className="text-xs">de {i.remainingUnits}</span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
